import { z } from 'zod';
import { currentUserSchema } from './auth.js';
import { userRoleSchema } from './enums.js';
import { shareLinkSchema, SHARE_SLUG_ALPHABET } from './share.js';
import { BULK_FILES_MAX, provenanceSchema, ttlKindSchema, uploadSkippedSchema, } from './storage.js';
/**
 * Contrats des LIENS COMMUNAUTAIRES (XFILE-08 ; ADR-0005 partie b) — source unique
 * front + back.
 *
 * Modèle T7 : PLUSIEURS liens communautaires TYPÉS coexistent (un actif par compte, plus
 * un lien de dépôt), gérés par le superadmin :
 *   - `account` : lien AUTHENTIFIANT adossé à un COMPTE existant — le visiteur agit SOUS
 *     l'identité de ce compte (rôle inclus, ex. un admin anonyme assumé), via une vraie
 *     session forcée sans saisie de mot de passe, révocable/rotable ;
 *   - `deposit` : lien de DÉPÔT SEUL — aucune session ouverte ; le visiteur ne peut QUE
 *     déposer des fichiers (rangés en `AAAA/MM` du compte système) et récupère en retour
 *     le lien de partage public. Distribuable, révocable/rotable.
 *
 * Le jeton est un LIEN distribuable (base62, entropie authentifiante). Sa révocation ou
 * sa rotation coupe immédiatement l'accès correspondant.
 */
/** Alphabet du jeton communautaire — base62, mutualisé avec les slugs de partage. */
export const COMMUNITY_TOKEN_ALPHABET = SHARE_SLUG_ALPHABET;
/** Longueur du jeton communautaire (22 car. base62 ≈ 131 bits — qualité authentifiante). */
export const COMMUNITY_TOKEN_LENGTH = 22;
/** Un jeton communautaire VALIDE : exactement 22 caractères base62. */
export const communityTokenSchema = z
    .string()
    .regex(/^[0-9A-Za-z]{22}$/, "Jeton d'accès communautaire invalide");
/** Chemin SPA d'échange d'un lien de compte (page publique `/join/<token>`). */
export function communityJoinPath(token) {
    return `/join/${token}`;
}
/** Chemin SPA de la page de dépôt public (`/deposit/<token>`). */
export const communityDepositPath = (token) => `/deposit/${token}`;
/**
 * Nature d'un lien communautaire :
 *  - `account` : lien lié à un COMPTE existant — le visiteur agit sous l'identité de ce
 *    compte (rôle inclus, admin anonyme assumé) via une session forcée ;
 *  - `deposit` : lien de DÉPÔT SEUL — aucune session ; upload uniquement vers `AAAA/MM`
 *    du compte système, renvoie le lien de partage public.
 */
export const communityLinkKindSchema = z.enum(['account', 'deposit']);
/**
 * Un lien communautaire actif tel qu'exposé à la console superadmin. Pour un lien
 * `deposit`, tous les `target*` sont `null` (aucun compte cible). Pour un lien `account`,
 * ils décrivent le compte sous lequel le visiteur agira.
 */
export const communityLinkSchema = z.object({
    id: z.uuid(),
    kind: communityLinkKindSchema,
    token: z.string(),
    url: z.string(),
    targetUserId: z.uuid().nullable(),
    targetEmail: z.string().nullable(),
    targetDisplayName: z.string().nullable(),
    targetRole: userRoleSchema.nullable(),
    /**
     * Restriction par IP d'un lien `deposit` (« dépôt API », défense en profondeur) : liste
     * d'IP/CIDR séparés par des virgules, ou `null` = aucune restriction. Toujours `null`
     * pour un lien `account`. La validation sémantique (IP/CIDR) est faite côté serveur.
     */
    allowedIp: z.string().nullable(),
    /**
     * PROVENANCE que ce lien IMPOSE aux dépôts qui passent par lui (intégration pnyx) : un lien
     * de dépôt pnyx dédié tague ses fichiers `pnyx`. Toujours `web` pour un lien `account` (non
     * utilisée). La console l'affiche.
     */
    provenance: provenanceSchema,
    createdAt: z.string(),
});
/**
 * Corps de `PUT /api/community/deposit/allowed-ip` (superadmin) — définit (ou EFFACE) la
 * restriction par IP du lien de dépôt. `allowedIp` = liste d'IP/CIDR séparés par des
 * virgules, ou `null`/chaîne vide pour lever la restriction. Le serveur valide chaque
 * entrée (IP/CIDR) ; le schéma ne borne que la forme/longueur.
 */
export const setDepositAllowedIpRequestSchema = z.object({
    allowedIp: z.string().max(400).nullable(),
    /**
     * PROVENANCE du lien de dépôt ciblé (intégration pnyx) : `web` (défaut) ou `pnyx`. Un lien
     * de dépôt actif coexiste PAR provenance ; la restriction IP doit donc être scopée (sinon
     * régler l'allowlist du dépôt `web` toucherait aussi le dépôt `pnyx`, et inversement).
     */
    provenance: provenanceSchema.optional(),
});
/**
 * Réponse de `GET /api/community` (superadmin) : l'interrupteur global d'accès
 * communautaire (`enabled`, reflet des réglages) et TOUS les liens actifs.
 */
export const communityOverviewSchema = z.object({
    enabled: z.boolean(),
    links: z.array(communityLinkSchema),
});
/**
 * Corps de `POST /api/community/links` (superadmin) — crée (ou ROTE, un actif par cible)
 * un lien communautaire. `kind: 'account'` cible un compte existant (`targetUserId`) ;
 * `kind: 'deposit'` crée le lien de dépôt (sans cible). Réponse = `communityLinkSchema`.
 */
export const createCommunityLinkRequestSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('account'), targetUserId: z.uuid() }),
    // La PROVENANCE d'un lien de dépôt (intégration pnyx) : `web` (défaut serveur) ou `pnyx`
    // (dépôt proxifié par pnyx qui tague ses fichiers `pnyx`). Un lien de dépôt actif coexiste
    // PAR provenance.
    z.object({ kind: z.literal('deposit'), provenance: provenanceSchema.optional() }),
]);
/** Corps de `POST /api/community/redeem` (PUBLIC) : échange un jeton contre une session. */
export const communityRedeemRequestSchema = z.object({
    token: communityTokenSchema,
});
/**
 * Réponse de `POST /api/community/redeem` — même forme que le login (`{ slot, user }`) :
 * le client mémorise le `slot` (par onglet) et l'utilisateur devient actif. La session
 * forgée adopte l'IDENTITÉ DU COMPTE CIBLE du lien (rôle inclus, ex. `admin`), et non plus
 * un compte système figé en lecture seule.
 */
export const communityRedeemResponseSchema = z.object({
    slot: z.number().int().min(0),
    user: currentUserSchema,
});
/**
 * Réponse de `GET /api/community/deposit/:token` (PUBLIC) — de quoi afficher la page de
 * dépôt : l'interrupteur global (`enabled`), le nom de l'application (`appName`), la validité
 * du jeton (`valid`) et les DURÉES de conservation proposées (`uploadTtlKinds` + défaut
 * pré-sélectionné `defaultTtlKind`, `null` = « aucun »). Ne révèle aucune donnée du compte
 * système ni réglage sensible.
 */
export const communityDepositInfoSchema = z.object({
    enabled: z.boolean(),
    appName: z.string(),
    valid: z.boolean(),
    uploadTtlKinds: z.array(ttlKindSchema).min(1),
    defaultTtlKind: ttlKindSchema.nullable(),
});
/**
 * CAPACITÉ D'EFFACEMENT rendue au déposant ANONYME juste après son dépôt (XFILE-37).
 *
 * Un déposant sans compte n'a AUCUNE identité : la seule façon de lui laisser retirer ce qu'il
 * vient de déposer (mauvais fichier, photo de trop) est de lui remettre une CAPABILITY — un jeton
 * OPAQUE, à durée de vie COURTE, dont la portée est figée SERVEUR sur les ids exacts de CE dépôt.
 *
 * Trois propriétés font tenir le modèle, et aucune n'est négociable :
 *  - **opaque** — la valeur n'encode rien de réutilisable ; ce n'est PAS un id interne. Exposer les
 *    ids de fichiers au déposant ouvrirait la porte à des tentatives sur les ids d'AUTRUI, alors
 *    que le dépôt communautaire n'a jamais rien exposé d'interne (cf. `communityDepositResultSchema`) ;
 *  - **borné dans le temps** (`expiresAt`) — c'est une RÉTRACTATION (« je me suis trompé »), pas un
 *    droit de gestion durable. Passée la fenêtre, le contenu appartient au dépôt comme avant ;
 *  - **de portée close** — le serveur ne supprimera JAMAIS que des fichiers de ce dépôt, quoi que
 *    le client envoie (garde vérifiée route-side, cf. `depositDeleteRequestSchema`).
 *
 * `expiresAt` (ISO 8601) sert l'UI seule : elle peut masquer l'affordance quand la fenêtre est
 * close. L'autorité reste le serveur — un jeton échu est refusé même si le client l'ignore.
 */
export const depositManageSchema = z.object({
    token: z.string().min(1),
    expiresAt: z.string(),
});
/**
 * Réponse de `POST /api/community/deposit/:token/upload` (PUBLIC, multipart) : le lien de partage
 * public (`share`, `null` si aucun fichier écrit) et les fichiers ignorés (`skipped`). Aucun
 * identifiant interne n'est exposé au déposant.
 *
 * `folder` et `manage` sont ADDITIFS (optionnels) — les clients antérieurs continuent de
 * typechecker et de fonctionner :
 *  - **`folder`** (XFILE-36) : le lien du DOSSIER DE LOT qui contient le dépôt. Il donne à l'écran
 *    de résultat le contenu à afficher (galerie, zip, partage individuel) SANS lui demander le
 *    moindre id interne. Sa raison d'être est la PARITÉ VISUELLE : jusqu'ici, un dépôt d'UN SEUL
 *    fichier n'avait pas de dossier de lot, donc pas de `/d/:slug` à montrer, donc un écran de
 *    résultat pauvre — alors qu'à deux fichiers il devenait riche. Désormais le lot existe dans les
 *    deux cas et `folder` le porte. `share` NE CHANGE PAS de sens pour autant : à un fichier il
 *    reste le lien FICHIER (octets bruts inline, BBCode `[img]`), qui est ce que le déposant veut
 *    coller ; à deux fichiers ou plus, `share` et `folder` désignent le même lien dossier ;
 *  - **`manage`** (XFILE-37) : la capacité d'effacement décrite ci-dessus, `null`/absente quand il
 *    n'y a rien à effacer (aucun fichier écrit).
 */
export const communityDepositResultSchema = z.object({
    share: shareLinkSchema.nullable(),
    folder: shareLinkSchema.nullable().optional(),
    manage: depositManageSchema.nullable().optional(),
    skipped: z.array(uploadSkippedSchema),
});
/**
 * Corps de `POST /api/community/deposit/manage/:token/delete` (PUBLIC, XFILE-37) : les fichiers
 * que le déposant retire de son propre dépôt, désignés par leurs ids.
 *
 * Ces ids ne sont PAS un secret : le déposant les a obtenus en listant SON dossier de lot par son
 * slug public (`GET /api/share/d/:slug`), qui les expose déjà. Ce qui autorise l'effacement est le
 * JETON, jamais la connaissance d'un id — et la route revérifie que CHAQUE id envoyé appartient à
 * la portée du jeton, sans quoi elle refuse le lot ENTIER (jamais d'effacement partiel).
 * Borné à {@link BULK_FILES_MAX}, comme les autres opérations groupées sur fichiers.
 */
export const depositDeleteRequestSchema = z.object({
    fileIds: z.array(z.uuid()).min(1).max(BULK_FILES_MAX),
});
/**
 * Réponse de `POST /api/community/deposit/manage/:token/delete` : le nombre de fichiers
 * RÉELLEMENT soft-deletés (un id déjà effacé n'est pas recompté → rejeu idempotent à `0`).
 *
 * L'ESTAMPILLE de lot (`deleted_batch`) posée par le soft-delete n'est PAS renvoyée, à la
 * différence de `bulkDeleteFilesResultSchema` : l'annulation (`POST /api/deletions/:batch/restore`)
 * est une route AUTHENTIFIÉE, et rendre le batch à un anonyme lui donnerait de quoi la tenter.
 * Décision produit assumée — le déposant efface, il n'annule pas.
 */
export const depositDeleteResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
//# sourceMappingURL=community.js.map